import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MarketingSeo from "../components/marketing/MarketingSeo";
import { BookDemoLink, MarketingTopBar, SiteFooter, ContactSalesModal } from "../components/marketing/shared";
import { MarketingMotion } from "../components/marketing/MarketingMotion";
import HeroCampusArt from "../components/marketing/HeroCampusArt";
import ButtonLink from "../components/marketing/ButtonLink";
import { CTA_PRIMARY_LABEL, CTA_SECONDARY_LABEL } from "../components/marketing/navigation";
import { ORGANIZATION_ID } from "../components/marketing/organizationSchema";
import { faqPageSchemaFor } from "./landingparts/marketingFaqSchema";
import AiraAISection from "../components/AiraAISection";
import { init as initAnalytics, track, trackOnView } from "../lib/analytics";
import { useMarketingContent } from "../lib/marketingContent";

const AiSuiteSection = React.lazy(() => import("./landingparts/AiSuiteSection"));

const AIRA_BREADCRUMBS = {
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://vidyapeeth360.com/" },
    { "@type": "ListItem", position: 2, name: "Aira AI", item: "https://vidyapeeth360.com/aira" },
  ],
  publisher: { "@id": ORGANIZATION_ID },
};

// PGC-11: the four questions buyers actually fear about school AI, answered with the SAME
// reviewed boundary language AiraAISection/AiSuiteSection already carry (permission-checked
// context · human confirmation · deterministic numbers · plan gating). The visible FAQ and
// the FAQPage schema node share this exact array (the ModulePage one-source pattern), so the
// search graph can never publish an answer the page does not show.
const AIRA_FAQ = [
  {
    q: "What data can Aira see?",
    a: "Aira checks the signed-in user, the active school and the relevant permissions before any supported source is used, so it answers only from permission-checked product context for that school. Parent Aira is designed to use the authenticated family's child scope, and the public marketing assistant grounds only on public content.",
  },
  {
    q: "What happens when the AI is wrong?",
    a: "Model-written text can be wrong, so Aira separates read-only answers from action proposals. Nothing material runs on Aira's own authority: a person reviews and confirms each supported action, the acting user and outcome are recorded, and material answers should be verified before you rely on them.",
  },
  {
    q: "Which numbers are deterministic?",
    a: "Operational numbers — fee totals, attendance counts, dues and similar signals — are computed from recorded school data and configured rules, not generated by a language model. Aira explains those deterministic results; the model writes the prose around them.",
  },
  {
    q: "How does per-plan availability work?",
    a: "Aira features follow your plan and stay off until you enable them. Availability also depends on the enabled module, the user's permission and any configured channel, and the pricing page's live feature comparison shows which plans include the AI suite.",
  },
];

const AIRA_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    AIRA_BREADCRUMBS,
    faqPageSchemaFor(AIRA_FAQ),
  ].filter(Boolean),
};

/* WP-20 / homepage-length decision: the extended Aira story (the guided chat demo +
   the AI & Automation suite) moved here from the homepage, which keeps a compact
   teaser at #aira-ai linking in. The demo section is static vetted content and always
   renders; the AI-suite block keeps its CMS copy + `sections.ai_suite` visibility,
   exactly as it had on the homepage. */
export default function AiraMarketingPage() {
  const navigate = useNavigate();
  // Shared session-memoized CMS read — the embedded SiteFooter uses the same cache, so the page
  // mounts with ONE /public/marketing request instead of two parallel identical ones.
  const cms = useMarketingContent();
  const [salesOpen, setSalesOpen] = useState(false);
  useEffect(() => {
    initAnalytics();
    return trackOnView("ai-suite", "ai_suite_view");
  }, []);
  const showAiSuite = cms?.sections?.ai_suite !== false;
  // Page copy is CMS-owned (cms.aira_page); literals below byte-mirror the backend DEFAULTS.
  const pageCms = cms?.aira_page || {};

  return (
    <MarketingMotion>
      <div className="min-h-screen bg-canvas text-ink flex flex-col landing-page" data-testid="aira-page">
        <MarketingSeo
          title="Aira for Schools — Governed AI Assistant | Vidyapeeth360"
          description="Try Aira's guided chat demo: permission-checked answers, human-approved actions and recorded evidence — AI inside your school's operating boundary."
          path="/aira"
          image="/og/og-aira.jpg"
          schema={AIRA_SCHEMA}
        />
        <MarketingTopBar onBookDemo={() => setSalesOpen(true)} />
        <main className="flex-1">
          {/* Scenic scroll stage (2026-08-22): the Aira campus painting rides as a sticky
              full-viewport backdrop under the header + guided demo + AI-suite sections, their
              fills in campus-glass so the artwork follows the scroll. Light theme only; dark
              restores solid fills and hides the stage. */}
          <div className="hero-campus-stage-wrap">
            <HeroCampusArt artwork="aira" stage veilClassName="hero-campus-veil-aira" />
            <section className="mx-auto max-w-6xl px-5 pt-10 sm:pt-14 pb-24 sm:pb-32 text-center">
              <h1 className="font-heading font-bold text-3xl sm:text-[40px] leading-[1.12] text-ink tracking-tight max-w-2xl mx-auto">
                {pageCms.heading || "Aira — AI, Governed by People."}
              </h1>
              <p className="mt-4 text-ink-2 text-[16px] sm:text-[17px] leading-relaxed max-w-2xl mx-auto">
                {pageCms.subhead || "One assistant across chat, voice and automation. Aira answers from permission-checked school data and prepares each action for a person to confirm; anything that runs on its own runs only under an automation your school switched on."}
              </p>
            </section>

          {/* The guided workflow demo (moved from the homepage). The "See Aira" CTA inside the
              AI-suite section scrolls back up to this anchor. */}
          <div id="aira-ai" aria-hidden="true" className="scroll-mt-24" />
          <AiraAISection onDemo={() => {
            track("demo_request", { placement: "aira_live_demo", context: "aira_ai", page: "/aira" });
            setSalesOpen(true);
          }} />

          {showAiSuite && (
            <React.Suspense fallback={<div className="min-h-[38rem]" aria-hidden="true" />}>
              {/* No inline #register-your-school section on this page — the register CTA must
                  navigate, never scroll to a missing anchor. */}
              <AiSuiteSection content={cms?.ai_suite} seeAiraVisible onRegister={() => navigate("/register")} />
            </React.Suspense>
          )}

          {/* PGC-11: the visible Aira FAQ — the exact array that feeds the FAQPage schema
              node above. Non-collapsing cards: every boundary answer stays scannable. */}
          <section aria-labelledby="aira-faq-heading" className="border-t border-line campus-glass-surface">
            <div className="mx-auto max-w-6xl px-5 py-14">
              <h2 id="aira-faq-heading" className="font-heading text-2xl sm:text-3xl font-bold text-ink tracking-tight max-w-2xl">
                The questions schools ask about AI.
              </h2>
              <div className="mt-8 grid gap-5 md:grid-cols-2" data-testid="aira-faq">
                {AIRA_FAQ.map((item) => (
                  <div key={item.q} className="rounded-2xl border border-line bg-canvas p-6">
                    <h3 className="font-heading font-semibold text-ink text-[16px] leading-snug">{item.q}</h3>
                    <p className="mt-2.5 text-[14px] leading-relaxed text-ink-2">{item.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Close — the sitewide CTA vocabulary, real links (WP-05/WP-07). */}
          <section aria-labelledby="aira-close-heading" className="border-t border-line campus-glass-muted60">
            <div className="mx-auto max-w-4xl px-5 py-14 text-center">
              <h2 id="aira-close-heading" className="font-heading text-2xl sm:text-3xl font-bold text-ink tracking-tight">
                {pageCms.close_heading || "See Aira on your school's own workflows"}
              </h2>
              <p className="mt-3 text-ink-2 max-w-xl mx-auto">
                {pageCms.close_subhead || "Start the 90-day trial — no card — or walk one workflow with us first. Aira features follow your plan and stay off until you enable them."}
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <ButtonLink to="/register" className="!rounded-full w-full sm:w-auto justify-center">{CTA_PRIMARY_LABEL}</ButtonLink>
                <BookDemoLink contextLabel="Aira AI on your school's workflows" placement="aira_page_close" variant="outline" className="!rounded-full w-full sm:w-auto justify-center">{CTA_SECONDARY_LABEL}</BookDemoLink>
              </div>
            </div>
          </section>
          </div>
        </main>
        <SiteFooter />
        <ContactSalesModal open={salesOpen} onClose={() => setSalesOpen(false)} contextLabel="Aira AI on your school's workflows" placement="aira_page" />
      </div>
    </MarketingMotion>
  );
}
